/**
 * MN Notes slider — peek carousel + decoration shake on view.
 * Slider behavior mirrors mn-testimonials-slider (drag, loop, autoplay).
 */
class MnNotesSlider extends HTMLElement {
  constructor() {
    super();
    this.index = 0;
    this.slides = [];
    this.track = null;
    this.viewport = null;
    this.prevButton = null;
    this.nextButton = null;
    this.autoplayTimer = null;
    this.resizeObserver = null;
    this.abortController = null;
    this.reduceMotion = null;
    this.hoverMedia = null;
    this.isPointerInside = false;
    this.pointerId = null;
    this.dragStartX = 0;
    this.dragDeltaX = 0;
    this.isDragging = false;
    this.dragMoved = false;

    this.goTo = this.goTo.bind(this);
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);
    this.update = this.update.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
  }

  connectedCallback() {
    this.cleanup();
    this.abortController = new AbortController();
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.track = this.querySelector('[data-mn-notes-track]');
    this.viewport = this.querySelector('.mn-notes-slider__viewport');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.slides = [...this.querySelectorAll('[data-mn-notes-slide]')];

    if (!(this.track instanceof HTMLElement) || this.slides.length === 0) return;

    const { signal } = this.abortController;

    this.prevButton?.addEventListener('click', this.prev, { signal });
    this.nextButton?.addEventListener('click', this.next, { signal });
    this.addEventListener('keydown', this.handleKeydown, { signal });
    document.addEventListener('visibilitychange', this.handleVisibility, { signal });

    const dragTarget = this.viewport instanceof HTMLElement ? this.viewport : this;
    dragTarget.addEventListener('pointerdown', this.onPointerDown, { signal });
    dragTarget.addEventListener('pointermove', this.onPointerMove, { signal });
    dragTarget.addEventListener('pointerup', this.onPointerUp, { signal });
    dragTarget.addEventListener('pointercancel', this.onPointerUp, { signal });
    dragTarget.addEventListener('lostpointercapture', this.onPointerUp, { signal });
    dragTarget.addEventListener('click', this.onClickCapture, { capture: true, signal });

    this.addEventListener(
      'pointerenter',
      (event) => {
        if (!this.canHoverPause || event.pointerType === 'touch') return;
        this.isPointerInside = true;
        this.stopAutoplay();
      },
      { signal }
    );
    this.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerType === 'touch') return;
        this.isPointerInside = false;
        if (!this.isDragging) this.startAutoplay();
      },
      { signal }
    );
    this.addEventListener('focusin', () => this.stopAutoplay(), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (this.contains(event.relatedTarget)) return;
        window.requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) this.startAutoplay();
        });
      },
      { signal }
    );

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', this.update, { signal });
    }

    this.setAttribute('tabindex', '0');
    this.syncArrowVisibility();
    this.goTo(0, false);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  get canHoverPause() {
    return this.hoverMedia?.matches === true;
  }

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true' && this.slides.length > 1;
  }

  get autoplaySpeed() {
    const speed = Number.parseInt(this.dataset.autoplaySpeed || '6000', 10);
    return Number.isFinite(speed) && speed >= 1000 ? speed : 6000;
  }

  get loopEnabled() {
    return this.dataset.loop === 'true' && this.slides.length > 1;
  }

  get showArrows() {
    return this.dataset.showArrows !== 'false' && this.slides.length > 1;
  }

  get transitionMs() {
    const duration = Number.parseInt(this.dataset.transition || '500', 10);
    return Number.isFinite(duration) && duration >= 0 ? duration : 500;
  }

  syncArrowVisibility() {
    this.updateArrowState();
  }

  updateArrowState() {
    if (!(this.prevButton instanceof HTMLButtonElement) || !(this.nextButton instanceof HTMLButtonElement)) {
      return;
    }

    const arrowsEnabled = this.showArrows;
    const multi = this.slides.length > 1;

    if (!arrowsEnabled || !multi) {
      this.prevButton.hidden = true;
      this.nextButton.hidden = true;
      this.prevButton.disabled = true;
      this.nextButton.disabled = true;
      return;
    }

    // Only show the arrow for a direction you can swipe toward.
    // Start: next only · Middle: both · End: prev only
    // (Independent of loop — avoids showing both arrows by default.)
    const hasPrev = this.index > 0;
    const hasNext = this.index < this.slides.length - 1;

    this.prevButton.hidden = !hasPrev;
    this.nextButton.hidden = !hasNext;
    this.prevButton.disabled = !hasPrev;
    this.nextButton.disabled = !hasNext;
  }

  slideStep() {
    const first = this.slides[0];
    if (!(first instanceof HTMLElement)) return 0;
    const styles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return first.getBoundingClientRect().width + gap;
  }

  baseOffset() {
    return this.slideStep() * this.index;
  }

  setTrackOffset(offset, animate = true) {
    if (!(this.track instanceof HTMLElement)) return;
    this.track.style.transitionDuration = animate && !this.reduceMotion?.matches ? `${this.transitionMs}ms` : '0ms';
    this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
  }

  goTo(index, animate = true) {
    if (this.slides.length === 0) return;

    let nextIndex = index;
    if (this.loopEnabled) {
      nextIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
    } else {
      nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    }

    this.index = nextIndex;
    this.setTrackOffset(this.baseOffset(), animate);

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.index;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.toggleAttribute('data-active', active);
      slide.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
    });

    this.updatePeekFade();
    this.updateArrowState();
  }

  updatePeekFade() {
    if (!(this.viewport instanceof HTMLElement)) return;
    // No peek on mobile (one slide fills the viewport)
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const hasNextPeek =
      !isMobile && this.slides.length > 1 && (this.loopEnabled || this.index < this.slides.length - 1);
    this.viewport.toggleAttribute('data-has-peek', hasNextPeek);
  }

  next() {
    if (this.loopEnabled) {
      this.goTo(this.index + 1);
    } else if (this.index < this.slides.length - 1) {
      this.goTo(this.index + 1);
    }
    this.restartAutoplay();
  }

  prev() {
    if (this.loopEnabled) {
      this.goTo(this.index - 1);
    } else if (this.index > 0) {
      this.goTo(this.index - 1);
    }
    this.restartAutoplay();
  }

  update() {
    this.goTo(this.index, false);
  }

  onPointerDown(event) {
    if (this.slides.length <= 1) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button, a')) return;

    // Stop native image drag (mouse) so swipe works on image slides too.
    if (
      event.pointerType !== 'touch' &&
      event.target instanceof Element &&
      event.target.closest('img, picture, .mn-notes-slider__media')
    ) {
      event.preventDefault();
    }

    this.isDragging = true;
    this.dragMoved = false;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragDeltaX = 0;
    this.stopAutoplay();

    const target = this.viewport instanceof HTMLElement ? this.viewport : this;
    target.setPointerCapture?.(event.pointerId);
    this.setTrackOffset(this.baseOffset(), false);
    target.classList.add('is-dragging');
  }

  onPointerMove(event) {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;
    this.dragDeltaX = event.clientX - this.dragStartX;
    if (Math.abs(this.dragDeltaX) > 6) this.dragMoved = true;
    this.setTrackOffset(this.baseOffset() - this.dragDeltaX, false);
  }

  onPointerUp(event) {
    if (!this.isDragging || (event.pointerId != null && event.pointerId !== this.pointerId)) return;

    const target = this.viewport instanceof HTMLElement ? this.viewport : this;
    target.classList.remove('is-dragging');
    target.releasePointerCapture?.(this.pointerId);

    const threshold = Math.min(80, (this.slideStep() || 240) * 0.18);
    if (this.dragDeltaX <= -threshold) this.next();
    else if (this.dragDeltaX >= threshold) this.prev();
    else this.goTo(this.index);

    this.isDragging = false;
    this.pointerId = null;
    this.dragDeltaX = 0;
    if (!this.isPointerInside) this.startAutoplay();
  }

  onClickCapture(event) {
    if (!this.dragMoved) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragMoved = false;
  }

  handleKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  }

  handleVisibility() {
    if (document.hidden) this.stopAutoplay();
    else this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayEnabled || this.reduceMotion?.matches || document.hidden) return;
    if (this.isPointerInside && this.canHoverPause) return;
    this.autoplayTimer = window.setInterval(() => this.next(), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer != null) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    this.startAutoplay();
  }

  cleanup() {
    this.stopAutoplay();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.abortController?.abort();
    this.abortController = null;
  }
}

customElements.get('mn-notes-slider') || customElements.define('mn-notes-slider', MnNotesSlider);

(function initNotesDecoration() {
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DESKTOP = window.matchMedia('(min-width: 990px)');

  function init(section) {
    if (!(section instanceof HTMLElement)) return;
    if (!section.querySelector('.mn-notes-slider__decoration')) return;

    const play = () => {
      if (REDUCE.matches || !DESKTOP.matches) return;
      section.classList.add('mn-notes-slider--deco-animate');
    };

    if (!('IntersectionObserver' in window)) {
      play();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          play();
          observer.disconnect();
          break;
        }
      },
      { threshold: 0.35, rootMargin: '0px' }
    );

    observer.observe(section);
  }

  function initAll() {
    document.querySelectorAll('.mn-notes-slider').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    if (!(root instanceof Element)) return;
    root.querySelectorAll('.mn-notes-slider').forEach(init);
  });
})();
